import { FC, Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from 'hooks/useToggle';
// Redux
import { useAppSelector } from 'hooks/useStore';
import { getGroupItemsForSelect } from 'store/group/groupSelectors';
import { getNamespaceItemsForSelect } from 'store/namespace/namespaceSelectors';
import QuizTypes from 'types/QuizTypes';
// Components
import { Input, Select } from 'components/Controls';
import DatePicker from 'components/DatePicker';
import NamespaceFormDialog from 'dialogs/NamespaceForm.dialog';
import PublicDevicePlayable from './PublicDevicePlayable';
import Languages from './Languages';
import Accordion from 'components/Accordion';
import CreateGroupForm from 'dialogs/CreateGroupForm.dialog';
// Utilities
import { isRequired, noWhitespace } from 'utilities/validation';

const SectionMain:FC = () => {
  const { t } = useTranslation();

  const { control, formState: { errors }, watch } = useFormContext();

  const namespaceOptions = useAppSelector(getNamespaceItemsForSelect);
  const groupOptions = useAppSelector(getGroupItemsForSelect);

  const { open:dialogNamespaceFormOpen, toggle:toggleNamespaceFormDialog } = useToggle();
  const { open:dialogQuizGroupFormOpen, toggle:toggleQuizGroupFormDialog } = useToggle();

  const watchedType = watch('type');
  const watchedStartsDate = watch('startsDate');

  return (
    <div react-scroll="main">
      <Accordion label={t('labels.main')} icon="list" defaultExpanded>
        <div className="w-full">
          <div className="-mt-3 mb-4">
            <button
              className="uppercase text-primary font-bold hover:underline"
              type="button"
              onClick={toggleNamespaceFormDialog}
            >{t('pages.quizForm.createCategoryButton')}</button>
          </div>
          <Controller
            control={control} name="namespaceId" rules={{ required: isRequired }}
            render={({ field }) => (
              <Select
                {...field}
                label={t('labels.category')} id="namespaceId" name="namespaceId"
                required={true}
                options={[
                  { value: '', label: t('labels.chooseCategory') },
                  ...namespaceOptions
                ]}
                error={Boolean(errors.namespaceId)}
                errorText={errors.namespaceId ? errors.namespaceId.message : ''}
                helperText={t('pages.quizForm.categoryHint')}
              />
            )}
          />
          
          <Languages />

          <Controller
            control={control} name="quizName" rules={{ required: isRequired, validate: noWhitespace }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('pages.quizForm.quizNameLabel')} id="quizName" name="quizName"
                required={true}
                error={Boolean(errors.quizName)}
                errorText={errors.quizName ? errors.quizName.message : ''}
                helperText={t('pages.quizForm.quizNameHint')}
              />
            )}
          />
          {[QuizTypes.Scoring, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(watchedType) && (
            <Fragment>
              <div className="mb-2">
                <button
                  className="uppercase text-primary font-bold hover:underline"
                  type="button"
                  onClick={toggleQuizGroupFormDialog}
                >{t('pages.groups.createButton')}</button>
              </div>
              <Controller
                control={control} name="quizGroup"
                render={({ field }) => (
                  <Select
                    {...field}
                    label={t('pages.quizForm.quizGroupLabel')} id="quizGroup" name="quizGroup"
                    options={[
                      { value: '', label: t('pages.quizForm.chooseQuizGroup') },
                      ...groupOptions
                    ]}
                    helperText={t('pages.quizForm.quizGroupHint')}
                  />
                )}
              />
            </Fragment>
          )}
          <div className="flex flex-wrap -mx-2">
            <div className="w-full sm:w-1/2 px-2">
              <div className="mb-4">
                <Controller
                  control={control} name="startsDate"
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label={t('labels.startDate')}
                      id="startsDate"
                    />
                  )}
                />
              </div>
            </div>
            <div className="w-full sm:w-1/2 px-2">
              <div className="mb-4">
                <Controller
                  control={control} name="endsDate"
                  rules={{ validate: (value) => {
                    if (watchedStartsDate && value && watchedStartsDate >= value) {
                      return t('validations.endDateMustBeGreaterThanStartDate');
                    }
                    return true;
                  } }}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label={t('labels.endDate')}
                      id="endsDate"
                      error={Boolean(errors.endsDate)}
                      errorText={errors.endsDate ? errors.endsDate.message : ''}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          {watchedType === QuizTypes.Prediction && (
            <span className="block text-gray-400 text-sm font-light leading-5 -mt-2 mb-4">
              {t('pages.quizForm.datesPredictionsHint')}
            </span>
          )}
          <PublicDevicePlayable />

          {dialogNamespaceFormOpen ? (
            <NamespaceFormDialog
              open={dialogNamespaceFormOpen}
              onClose={toggleNamespaceFormDialog}
            />
          ) : null}
          {dialogQuizGroupFormOpen ? (
            <CreateGroupForm
              open={dialogQuizGroupFormOpen}
              onClose={toggleQuizGroupFormDialog}
            />
          ) : null}
        </div>
      </Accordion>
    </div>
  )
}

export default SectionMain;
